import React from 'react';

/* Article hero illustrations. Wrapped by .blog-figure in BlogArticle.
   Accent strokes use currentColor; palette, fonts and the paper background
   are set in src/styles/pages/blog.css (.blog-illo). Decorative: the article
   text carries the same information. */
const wrapProps = { className: 'blog-illo blog-illo--hero', 'aria-hidden': 'true' };

/* ─────────────────────────────────────────────
   ADM Directive — Compliance countdown / shield
   ───────────────────────────────────────────── */
export function ADMDirectiveHero() {
  return (
    <div {...wrapProps}>
      <svg viewBox="0 0 860 260" fill="none">
        <rect className="illo-bg" width="860" height="260" />

        {/* Background grid dots */}
        {Array.from({ length: 12 }).map((_, row) =>
          Array.from({ length: 24 }).map((_, col) => (
            <circle key={`${row}-${col}`} cx={40 + col * 34} cy={20 + row * 22} r="1" fill="#1A1714" opacity=".06" />
          ))
        )}

        {/* Central shield */}
        <path d="M400 50 L400 170 Q400 210 430 225 Q460 210 460 170 L460 50 Q430 38 400 50Z"
          stroke="currentColor" strokeWidth="2.5" fill="currentColor" fillOpacity=".07" />
        <path d="M418 130 L428 142 L452 112" stroke="currentColor" strokeWidth="3.5" fill="none"
          strokeLinecap="round" strokeLinejoin="round" opacity=".7" />
        {/* Shield label */}
        <text x="430" y="90" textAnchor="middle" fontSize="11" fontFamily="'Inter', sans-serif"
          fill="currentColor" fontWeight="600" opacity=".5">AIA</text>

        {/* Left — countdown calendar */}
        <rect x="80" y="65" width="120" height="110" rx="10" stroke="#1A1714" strokeWidth="2" fill="#FFFFFF" opacity=".9" />
        <rect x="80" y="65" width="120" height="30" rx="10" fill="#1A1714" opacity=".08" />
        <text x="140" y="85" textAnchor="middle" fontSize="11" fontFamily="'Inter', sans-serif"
          fill="#1A1714" fontWeight="600" opacity=".4">JUNE 2026</text>
        <text x="140" y="145" textAnchor="middle" fontSize="38" fontFamily="'Space Grotesk', sans-serif"
          fill="currentColor" fontWeight="700" opacity=".7">24</text>
        <text x="140" y="165" textAnchor="middle" fontSize="10" fontFamily="'Inter', sans-serif"
          fill="#6B6560" fontWeight="500">DEADLINE</text>

        {/* Connecting dashed line from calendar to shield */}
        <line x1="205" y1="120" x2="395" y2="120" stroke="currentColor" strokeWidth="1.5"
          strokeDasharray="6 4" opacity=".2" />

        {/* Right — impact levels stack */}
        <g>
          {['I', 'II', 'III', 'IV'].map((level, i) => {
            const y = 68 + i * 28;
            const w = 70 + i * 18;
            const opacity = 0.08 + i * 0.06;
            return (
              <g key={level}>
                <rect x={640} y={y} width={w} height={22} rx={6}
                  fill="currentColor" opacity={opacity} />
                <text x={648} y={y + 15} fontSize="10" fontFamily="'Inter', sans-serif"
                  fill="#1A1714" fontWeight="600" opacity=".5">Level {level}</text>
              </g>
            );
          })}
          <text x="640" y="190" fontSize="9" fontFamily="'Inter', sans-serif"
            fill="#6B6560" opacity=".5">Impact Levels</text>
        </g>

        {/* Connecting line from shield to levels */}
        <line x1="465" y1="120" x2="635" y2="120" stroke="currentColor" strokeWidth="1.5"
          strokeDasharray="6 4" opacity=".2" />

        {/* Bottom accent bar */}
        <rect x="0" y="254" width="860" height="6" fill="currentColor" opacity=".08" />

        {/* Floating badges */}
        <rect x="260" y="200" width="100" height="24" rx="12" fill="#1A1714" opacity=".06" />
        <text x="310" y="216" textAnchor="middle" fontSize="9" fontFamily="'Inter', sans-serif"
          fill="#1A1714" fontWeight="600" opacity=".35">Transparency</text>

        <rect x="500" y="200" width="85" height="24" rx="12" fill="#1A1714" opacity=".06" />
        <text x="542" y="216" textAnchor="middle" fontSize="9" fontFamily="'Inter', sans-serif"
          fill="#1A1714" fontWeight="600" opacity=".35">Oversight</text>
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Connected Care — Data flowing between provinces
   ───────────────────────────────────────────── */
export function ConnectedCareHero() {
  return (
    <div {...wrapProps}>
      <svg viewBox="0 0 860 260" fill="none">
        <rect className="illo-bg" width="860" height="260" />

        {/* Background subtle circles */}
        <circle cx="430" cy="130" r="110" stroke="currentColor" strokeWidth="1" opacity=".06" />
        <circle cx="430" cy="130" r="80" stroke="currentColor" strokeWidth="1" opacity=".04" />

        {/* Province nodes */}
        {[
          { cx: 150, cy: 100, label: 'ON', r: 32 },
          { cx: 340, cy: 60, label: 'QC', r: 26 },
          { cx: 520, cy: 60, label: 'BC', r: 26 },
          { cx: 710, cy: 100, label: 'AB', r: 32 },
          { cx: 280, cy: 180, label: 'NS', r: 22 },
          { cx: 580, cy: 180, label: 'MB', r: 22 },
        ].map(({ cx, cy, label, r }) => (
          <g key={label}>
            <circle cx={cx} cy={cy} r={r} stroke="#1A1714" strokeWidth="2" fill="#FFFFFF" />
            <circle cx={cx} cy={cy} r={r - 6} fill="currentColor" opacity=".08" />
            <text x={cx} y={cy + 4} textAnchor="middle" fontSize="12" fontFamily="'Inter', sans-serif"
              fill="#1A1714" fontWeight="700" opacity=".45">{label}</text>
          </g>
        ))}

        {/* Connection lines between nodes */}
        {[
          [150, 100, 340, 60],
          [340, 60, 520, 60],
          [520, 60, 710, 100],
          [150, 100, 280, 180],
          [710, 100, 580, 180],
          [280, 180, 580, 180],
          [340, 60, 280, 180],
          [520, 60, 580, 180],
        ].map(([x1, y1, x2, y2], i) => (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="currentColor" strokeWidth="1.5" opacity=".12" />
        ))}

        {/* Central FHIR badge */}
        <rect x="385" y="110" width="90" height="36" rx="18" fill="#FFFFFF"
          stroke="currentColor" strokeWidth="2" />
        <text x="430" y="133" textAnchor="middle" fontSize="13" fontFamily="'Space Grotesk', sans-serif"
          fill="currentColor" fontWeight="700">HL7 FHIR</text>

        {/* Data flow arrows */}
        {[
          { x: 215, y: 78, rot: -20 },
          { x: 630, y: 78, rot: 20 },
          { x: 330, y: 140, rot: -65 },
          { x: 530, y: 140, rot: 65 },
        ].map(({ x, y, rot }, i) => (
          <g key={i} transform={`translate(${x},${y}) rotate(${rot})`} opacity=".25">
            <line x1="0" y1="0" x2="25" y2="0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <polyline points="20,-4 25,0 20,4" stroke="currentColor" strokeWidth="2" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}

        {/* Bottom labels */}
        <text x="430" y="240" textAnchor="middle" fontSize="10" fontFamily="'Inter', sans-serif"
          fill="#6B6560" opacity=".4" fontWeight="500" letterSpacing="0.1em">
          CROSS-PROVINCIAL DATA SHARING
        </text>

        {/* Small lock icon for consent */}
        <rect x="55" y="195" width="50" height="40" rx="8" fill="#FFFFFF"
          stroke="#1A1714" strokeWidth="1.5" opacity=".12" />
        <rect x="68" y="185" width="24" height="16" rx="8" fill="none"
          stroke="#1A1714" strokeWidth="1.5" opacity=".12" />
        <circle cx="80" cy="215" r="3" fill="currentColor" opacity=".25" />

        {/* Small document icon */}
        <rect x="760" y="170" width="38" height="48" rx="4" fill="none"
          stroke="#1A1714" strokeWidth="1.5" opacity=".1" />
        <line x1="768" y1="184" x2="790" y2="184" stroke="#1A1714" strokeWidth="1" opacity=".1" />
        <line x1="768" y1="192" x2="786" y2="192" stroke="#1A1714" strokeWidth="1" opacity=".1" />
        <line x1="768" y1="200" x2="788" y2="200" stroke="#1A1714" strokeWidth="1" opacity=".1" />
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Clinic AI — 7 workflows pipeline
   ───────────────────────────────────────────── */
export function ClinicAIHero() {
  const workflows = [
    { icon: 'M12 4v16m-4-4l4 4 4-4', label: 'Intake', color: 'currentColor' },
    { icon: 'M8 7h8m-8 4h8m-8 4h5', label: 'Reminders', color: 'currentColor' },
    { icon: 'M9 5l7 7-7 7', label: 'Referrals', color: 'currentColor' },
    { icon: 'M4 12h16M12 4v16', label: 'Follow-up', color: 'currentColor' },
    { icon: 'M3 7h4l3 9h6l3-9h4', label: 'Filing', color: 'currentColor' },
    { icon: 'M8 10h8m-4-4v8', label: 'FAQ Bot', color: 'currentColor' },
    { icon: 'M3 3h18v18H3z M7 12h10', label: 'Dashboard', color: 'currentColor' },
  ];

  return (
    <div {...wrapProps}>
      <svg viewBox="0 0 860 240" fill="none">
        <rect className="illo-bg" width="860" height="240" />

        {/* Timeline backbone */}
        <line x1="60" y1="120" x2="800" y2="120" stroke="#E0DBD3" strokeWidth="2" />

        {/* 7 workflow nodes */}
        {workflows.map((wf, i) => {
          const cx = 100 + i * 105;
          const isTop = i % 2 === 0;
          const nodeY = 120;
          const labelY = isTop ? 58 : 182;
          const lineY1 = isTop ? 85 : 155;
          const lineY2 = isTop ? nodeY - 22 : nodeY + 22;

          return (
            <g key={i}>
              {/* Connector line */}
              <line x1={cx} y1={lineY1} x2={cx} y2={lineY2}
                stroke="currentColor" strokeWidth="1.5" opacity=".2" />

              {/* Node circle */}
              <circle cx={cx} cy={nodeY} r="20" fill="#FFFFFF"
                stroke={wf.color} strokeWidth="2" />
              <text x={cx} y={124} textAnchor="middle" fontSize="16"
                fontFamily="'Space Grotesk', sans-serif" fill={wf.color} fontWeight="700">
                {i + 1}
              </text>

              {/* Label pill */}
              <rect x={cx - 38} y={labelY - 12} width="76" height="24" rx="12"
                fill={wf.color} opacity=".08" />
              <text x={cx} y={labelY + 3} textAnchor="middle" fontSize="10"
                fontFamily="'Inter', sans-serif" fill="#1A1714" fontWeight="600" opacity=".55">
                {wf.label}
              </text>
            </g>
          );
        })}

        {/* Arrow tips between nodes */}
        {Array.from({ length: 6 }).map((_, i) => {
          const x = 100 + i * 105 + 52;
          return (
            <g key={i} opacity=".2">
              <polyline points={`${x - 4},116 ${x},120 ${x - 4},124`}
                stroke="currentColor" strokeWidth="2" fill="none"
                strokeLinecap="round" strokeLinejoin="round" />
            </g>
          );
        })}

        {/* 30-day badge */}
        <rect x="745" y="163" width="100" height="38" rx="19" fill="#FFFFFF"
          stroke="currentColor" strokeWidth="1.5" />
        <text x="795" y="179" textAnchor="middle" fontSize="14"
          fontFamily="'Space Grotesk', sans-serif" fill="currentColor" fontWeight="700">30 days</text>
        <text x="795" y="192" textAnchor="middle" fontSize="8"
          fontFamily="'Inter', sans-serif" fill="#6B6560" fontWeight="500">to deploy</text>

        {/* Checkmark at end */}
        <circle cx="820" cy="120" r="14" fill="currentColor" opacity=".1" />
        <path d="M813 120l4 4 8-8" stroke="currentColor" strokeWidth="2.5" fill="none"
          strokeLinecap="round" strokeLinejoin="round" opacity=".5" />

        {/* Bottom accent */}
        <rect x="0" y="234" width="860" height="6" fill="currentColor" opacity=".06" />
      </svg>
    </div>
  );
}
